"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";

import type { HomeLecturer } from "@/templates/site/types";

type LecturersProps = {
  items: HomeLecturer[];
};

export function HomeLecturers({ items }: LecturersProps) {
  const t = useTranslations("site.homePage.lecturers");
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = useCallback(() => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    setAtStart(track.scrollLeft <= 2);
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 2);
  }, []);

  useEffect(() => {
    syncEdges();
    window.addEventListener("resize", syncEdges);

    return () => window.removeEventListener("resize", syncEdges);
  }, [syncEdges]);

  const scrollByCell = (direction: 1 | -1) => {
    const track = trackRef.current;
    const cell = track?.firstElementChild;

    if (!track || !cell) {
      return;
    }

    track.scrollBy({ left: direction * (cell.clientWidth + 30) });
  };

  return (
    <section className="shouye-jiangshi" aria-labelledby="shouye-jiangshi-title">
      <div className="w1440 shouye-jiangshi__inner">
        <header className="shouye-jiangshi__head">
          <p className="shouye-jiangshi__kicker">{t("kicker")}</p>
          <h2 id="shouye-jiangshi-title" className="shouye-jiangshi__title">
            {t("title")}
          </h2>
        </header>

        <div
          className="shouye-jiangshi__track"
          ref={trackRef}
          onScroll={syncEdges}
        >
          {items.map((item) => (
            <div key={item.id} className="shouye-jiangshi__cell">
              <article className="shouye-jiangshi__card">
                <div className="shouye-jiangshi__intro">
                  <h3 className="shouye-jiangshi__name">
                    <Link href={item.href}>{item.name}</Link>
                  </h3>
                  {item.org ? (
                    <p className="shouye-jiangshi__org">{item.org}</p>
                  ) : null}
                </div>
                {item.photo ? (

                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    className="shouye-jiangshi__photo"
                    src={item.photo}
                    alt={item.name}
                    loading="lazy"
                  />
                ) : null}
                {item.intro ? (
                  <div className="shouye-jiangshi__quote">
                    <p className="shouye-jiangshi__quote-text">{item.intro}</p>
                  </div>
                ) : null}
              </article>
            </div>
          ))}
        </div>

        {items.length > 1 ? (
          <div className="shouye-jiangshi__nav">
            <button
              type="button"
              className="shouye-jiangshi__btn shouye-jiangshi__btn--prev"
              aria-label={t("prev")}
              disabled={atStart}
              onClick={() => scrollByCell(-1)}
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="shouye-jiangshi__btn shouye-jiangshi__btn--next"
              aria-label={t("next")}
              disabled={atEnd}
              onClick={() => scrollByCell(1)}
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
